import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageCard,
  ListPageFilters,
  ListPageStats,
  ListPageContent,
} from '@/components/ui/list-page';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import { StatusBadge } from '@/components/ui/status-badge';
import { useApp } from '@/components/AppLayout';
import { paymentApi, clientApi, queryFn, type Payment, type Client, type PaymentMethod } from '@/lib/api';
import { formatAmount, formatAmountWithSmallCents } from '@/lib/currency';
import {
  Banknote,
  CalendarIcon,
  Users,
  X,
  Receipt,
  CreditCard,
  Building2,
  Smartphone,
  FileText,
  Coins,
  CircleDollarSign,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths, subDays } from 'date-fns';

type DateRangePreset = 'all' | 'last_7' | 'last_30' | 'last_month' | 'this_month' | 'last_12' | 'ytd' | 'custom';
type AllocationStatus = 'all' | 'fully_allocated' | 'partially_allocated' | 'unallocated';

// Payment method icon mapping
const getPaymentMethodIcon = (method: PaymentMethod) => {
  switch (method) {
    case 'check':
      return <Receipt className="h-4 w-4" />;
    case 'card':
      return <CreditCard className="h-4 w-4" />;
    case 'ach':
      return <Building2 className="h-4 w-4" />;
    case 'cash':
      return <Banknote className="h-4 w-4" />;
    case 'cash_app':
    case 'paypal':
    case 'venmo':
    case 'zelle':
      return <Smartphone className="h-4 w-4" />;
    case 'credit_memo':
      return <FileText className="h-4 w-4" />;
    case 'stripe':
      return <CreditCard className="h-4 w-4" />;
    case 'other':
    default:
      return <Coins className="h-4 w-4" />;
  }
};

// Payment method display labels
const paymentMethodLabels: Record<PaymentMethod, string> = {
  check: 'Check',
  card: 'Card',
  ach: 'ACH',
  cash: 'Cash',
  cash_app: 'Cash App',
  paypal: 'PayPal',
  venmo: 'Venmo',
  zelle: 'Zelle',
  credit_memo: 'Credit Memo',
  stripe: 'Stripe',
  other: 'Other',
};

// Helper to determine allocation status
const getAllocationStatus = (payment: Payment): 'fully_allocated' | 'partially_allocated' | 'unallocated' => {
  const unallocated = parseFloat(payment.unallocated_amount);
  const total = parseFloat(payment.amount);

  if (unallocated === 0) return 'fully_allocated';
  if (unallocated === total) return 'unallocated';
  return 'partially_allocated';
};

function PaymentsContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethod | 'all'>('all');
  const [allocationFilter, setAllocationFilter] = useState<AllocationStatus>('all');
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('all');
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<DateRangePreset>('all');
  const [pendingCustomRange, setPendingCustomRange] = useState<{ from?: Date; to?: Date }>({});
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);

  // Client filter state
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [pendingClientIds, setPendingClientIds] = useState<string[]>([]);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', currentBusiness?.id],
    queryFn: () => queryFn(clientApi.list(currentBusiness!.id)),
    enabled: !!currentBusiness,
  });

  const getDateRangeParams = () => {
    const today = new Date();
    let startDate: string | undefined;
    let endDate: string | undefined;

    switch (dateRangePreset) {
      case 'last_7':
        startDate = format(subDays(today, 7), 'yyyy-MM-dd');
        break;
      case 'last_30':
        startDate = format(subDays(today, 30), 'yyyy-MM-dd');
        break;
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        startDate = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        endDate = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      case 'this_month':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        endDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'last_12':
        startDate = format(subMonths(today, 12), 'yyyy-MM-dd');
        break;
      case 'ytd':
        startDate = format(startOfYear(today), 'yyyy-MM-dd');
        break;
      case 'custom':
        if (customDateRange.from) {
          startDate = format(customDateRange.from, 'yyyy-MM-dd');
        }
        if (customDateRange.to) {
          endDate = format(customDateRange.to, 'yyyy-MM-dd');
        }
        break;
    }

    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRangeParams();
  const clientId = selectedClientIds.length === 1 ? selectedClientIds[0] : undefined;
  const paymentMethod = paymentMethodFilter !== 'all' ? paymentMethodFilter : undefined;

  const { data: rawPayments = [], isLoading } = useQuery({
    queryKey: ['payments', currentBusiness?.id, clientId, paymentMethod, startDate, endDate],
    queryFn: () => queryFn(paymentApi.list(currentBusiness!.id, clientId, paymentMethod, startDate, endDate)),
    enabled: !!currentBusiness,
  });

  // All payments for stats (separate query)
  const { data: allPayments = [] } = useQuery({
    queryKey: ['payments-all', currentBusiness?.id],
    queryFn: () => queryFn(paymentApi.list(currentBusiness!.id)),
    enabled: !!currentBusiness,
  });

  // Client-side filtering
  const payments = useMemo(() => {
    let filtered = rawPayments.filter(p => p.payment_method !== 'credit_memo');
    if (selectedClientIds.length > 1) {
      filtered = filtered.filter(p => selectedClientIds.includes(p.client));
    }
    if (allocationFilter !== 'all') {
      filtered = filtered.filter(p => getAllocationStatus(p) === allocationFilter);
    }
    return filtered;
  }, [rawPayments, selectedClientIds, allocationFilter]);

  // Stats from all payments
  const { totalReceivedThisMonth, totalUnallocated } = useMemo(() => {
    const nonCreditMemo = allPayments.filter(p => p.payment_method !== 'credit_memo');
    const today = new Date();
    const monthStart = startOfMonth(today);
    const monthEnd = endOfMonth(today);
    const thisMonthPayments = nonCreditMemo.filter(p => {
      const paymentDate = new Date(p.payment_date);
      return paymentDate >= monthStart && paymentDate <= monthEnd;
    });
    const thisMonthTotal = thisMonthPayments.reduce((sum: number, p: Payment) => sum + parseFloat(p.amount), 0);
    const unallocatedTotal = nonCreditMemo.reduce((sum: number, p: Payment) => sum + parseFloat(p.unallocated_amount), 0);
    return {
      totalReceivedThisMonth: thisMonthTotal.toFixed(2),
      totalUnallocated: unallocatedTotal.toFixed(2),
    };
  }, [allPayments]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const columns: ColumnDef<Payment>[] = [
    {
      accessorKey: 'payment_date',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDate(row.getValue('payment_date'))}
        </span>
      ),
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.client_name || '-'}</div>
        </div>
      ),
    },
    {
      accessorKey: 'payment_method',
      header: 'Method',
      cell: ({ row }) => {
        const method = row.getValue('payment_method') as PaymentMethod;
        return (
          <div className="flex items-center gap-2 text-sm">
            {getPaymentMethodIcon(method)}
            <span>{paymentMethodLabels[method] || method}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'reference',
      header: 'Reference',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.getValue('reference') || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Amount" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-sm font-medium">
          {formatAmount(row.getValue('amount'))}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const payment = row.original;
        const config: Record<string, { dot: 'gray' | 'blue' | 'green' | 'orange' | 'red'; label: string }> = {
          completed: { dot: 'green', label: 'Completed' },
          pending: { dot: 'orange', label: 'Processing' },
          failed: { dot: 'red', label: 'Failed' },
        };
        const statusConfig = config[payment.status] || config.completed;
        return <StatusBadge dot={statusConfig.dot} label={statusConfig.label} />;
      },
    },
  ];

  const hasFilters = paymentMethodFilter !== 'all' || allocationFilter !== 'all' || dateRangePreset !== 'all' || selectedClientIds.length > 0;

  const receivedThisMonth = formatAmountWithSmallCents(totalReceivedThisMonth);
  const unallocatedAmount = formatAmountWithSmallCents(totalUnallocated);

  return (
      <ListPage>
        {/* Header */}
        <ListPageHeader>
          <ListPageTitle>Payments</ListPageTitle>
        </ListPageHeader>

        <ListPageCard>
          {/* Filters */}
          <ListPageFilters className="flex-wrap">
        <Select value={paymentMethodFilter} onValueChange={(value) => setPaymentMethodFilter(value as PaymentMethod | 'all')}>
          <SelectTrigger className="w-[180px] rounded-full">
            <SelectValue placeholder="All Methods" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="check">Check</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="ach">ACH</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="cash_app">Cash App</SelectItem>
            <SelectItem value="paypal">PayPal</SelectItem>
            <SelectItem value="venmo">Venmo</SelectItem>
            <SelectItem value="zelle">Zelle</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>

        <Popover
          open={datePopoverOpen}
          onOpenChange={(open) => {
            setDatePopoverOpen(open);
            if (open) {
              setPendingPreset(dateRangePreset);
              setPendingCustomRange(customDateRange);
              setShowCalendarPicker(false);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[220px] justify-start text-left font-normal rounded-full">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRangePreset === 'all' && 'All Time'}
              {dateRangePreset === 'last_7' && 'Last 7 Days'}
              {dateRangePreset === 'last_30' && 'Last 30 Days'}
              {dateRangePreset === 'last_month' && 'Last Month'}
              {dateRangePreset === 'this_month' && 'This Month'}
              {dateRangePreset === 'last_12' && 'Last 12 Months'}
              {dateRangePreset === 'ytd' && 'Year to Date'}
              {dateRangePreset === 'custom' && (
                customDateRange.from ? (
                  customDateRange.to ? (
                    `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d, yyyy')}`
                  ) : (
                    format(customDateRange.from, 'MMM d, yyyy')
                  )
                ) : (
                  'Custom Range'
                )
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className={showCalendarPicker ? "w-auto p-0" : "w-[220px] p-0"} align="start">
            {!showCalendarPicker ? (
              <div className="p-2">
                {[
                  { value: 'all', label: 'All Time' },
                  { value: 'last_7', label: 'Last 7 Days' },
                  { value: 'last_30', label: 'Last 30 Days' },
                  { value: 'last_month', label: 'Last Month' },
                  { value: 'this_month', label: 'This Month' },
                  { value: 'last_12', label: 'Last 12 Months' },
                  { value: 'ytd', label: 'Year to Date' },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={pendingPreset === option.value ? 'secondary' : 'ghost'}
                    className="w-full justify-start"
                    onClick={() => setPendingPreset(option.value as DateRangePreset)}
                  >
                    {option.label}
                  </Button>
                ))}
                <Button
                  variant={pendingPreset === 'custom' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setShowCalendarPicker(true)}
                >
                  Custom Range
                </Button>
                <div className="border-t mt-2 pt-2">
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setDateRangePreset(pendingPreset);
                      setDatePopoverOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3">
                <Calendar
                  mode="range"
                  selected={{ from: pendingCustomRange.from, to: pendingCustomRange.to }}
                  onSelect={(range) => setPendingCustomRange({ from: range?.from, to: range?.to })}
                  numberOfMonths={2}
                />
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setShowCalendarPicker(false);
                      setPendingCustomRange({});
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setDateRangePreset('custom');
                      setCustomDateRange(pendingCustomRange);
                      setDatePopoverOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Client Filter */}
        <Popover
          open={clientPopoverOpen}
          onOpenChange={(open) => {
            setClientPopoverOpen(open);
            if (open) {
              setPendingClientIds(selectedClientIds);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-start text-left font-normal gap-2 rounded-full">
              <Users className="h-4 w-4" />
              {selectedClientIds.length === 0 ? (
                'All Clients'
              ) : selectedClientIds.length === 1 ? (
                clients.find(c => c.id === selectedClientIds[0])?.billing_name || '1 client'
              ) : (
                `${selectedClientIds.length} clients`
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search clients..." />
              <CommandList>
                <CommandEmpty>No clients found.</CommandEmpty>
                <CommandGroup>
                  {clients.map((client) => (
                    <CommandItem
                      key={client.id}
                      onSelect={() => {
                        setPendingClientIds(prev =>
                          prev.includes(client.id)
                            ? prev.filter(id => id !== client.id)
                            : [...prev, client.id]
                        );
                      }}
                    >
                      <Checkbox
                        checked={pendingClientIds.includes(client.id)}
                        className="mr-2"
                      />
                      <div className="flex flex-col">
                        <span>{client.billing_name}</span>
                        {client.company_name && (
                          <span className="text-xs text-muted-foreground">{client.company_name}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
              <div className="border-t p-2 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setPendingClientIds([]);
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setSelectedClientIds(pendingClientIds);
                    setClientPopoverOpen(false);
                  }}
                >
                  Apply
                </Button>
              </div>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Allocation Status Filter */}
        <Select value={allocationFilter} onValueChange={(value) => setAllocationFilter(value as AllocationStatus)}>
          <SelectTrigger className="w-[180px] rounded-full">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="fully_allocated">Applied</SelectItem>
            <SelectItem value="partially_allocated">Partial</SelectItem>
            <SelectItem value="unallocated">Unallocated</SelectItem>
          </SelectContent>
        </Select>

        {(paymentMethodFilter !== 'all' || dateRangePreset !== 'all' || selectedClientIds.length > 0 || allocationFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPaymentMethodFilter('all');
              setDateRangePreset('all');
              setCustomDateRange({});
              setSelectedClientIds([]);
              setAllocationFilter('all');
            }}
            className="text-muted-foreground rounded-full"
          >
            <X className="h-4 w-4" />
            Clear all filters
          </Button>
        )}
          </ListPageFilters>

          {/* Stats */}
          <ListPageStats>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Received this month</div>
              <div className="text-2xl font-medium text-green-600">
                {receivedThisMonth.dollars}
                <span className="text-lg text-green-600/70">{receivedThisMonth.cents}</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Unallocated</div>
              <div className="text-2xl font-medium">
                {unallocatedAmount.dollars}
                <span className="text-lg text-muted-foreground">{unallocatedAmount.cents}</span>
              </div>
            </div>
          </ListPageStats>

          {/* Table */}
          <ListPageContent>
            <DataTable
              columns={columns}
              data={payments}
              loading={isLoading}
              onRowClick={(payment) => nav(`/payments/${payment.id}`)}
              noBorder
              emptyIcon={<CircleDollarSign className="h-6 w-6 text-muted-foreground" />}
              emptyTitle={hasFilters ? "No payments found" : "No payments yet"}
              emptyDescription={hasFilters ? "Try adjusting your filters." : "Payments will appear here when recorded."}
            />
          </ListPageContent>
        </ListPageCard>
      </ListPage>
  );
}

export function Payments() {
  return <PaymentsContent />;
}
